import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  GET_FAILED_PARTICIPATIONS_FOR_FORFEIT,
  FORFEIT_DEPOSIT,
} from "../api/review";

// 타입 정의
type ForfeitItem = {
  participationId: number;
  expiredStepCount: number;
  notApprovedStepCount: number;
  participation: {
    id: number;
    userId: number;
    status: string;
    joinedAt: string;
    depositAmount: number;
    prizeAmount: number;
    user: {
      id: number;
      name: string | null;
      phone: string | null;
    };
    run: {
      id: number;
      startAt: string;
      endAt: string;
      campaign: {
        id: number;
        productName: string;
        productImageUrl: string | null;
        brand: {
          name: string;
        };
        steps: Array<{
          id: number;
          stepOrder: number;
          title: string | null;
        }>;
      };
    };
  };
};

interface UserGroup {
  user: ForfeitItem["participation"]["user"];
  items: ForfeitItem[];
}

interface CampaignGroup {
  campaign: ForfeitItem["participation"]["run"]["campaign"];
  users: UserGroup[];
}

const FailuresList = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<{
    campaignId: number | null;
    userId: number | null;
  }>({
    campaignId: null,
    userId: null,
  });
  const [showForfeitModal, setShowForfeitModal] = useState(false);
  const [forfeitReason, setForfeitReason] = useState("");
  const [selectedParticipationId, setSelectedParticipationId] =
    useState<number | null>(null);

  const { data, loading, error, refetch } = useQuery(
    GET_FAILED_PARTICIPATIONS_FOR_FORFEIT,
    {
      variables: {
        filter: {
          campaignId: filter.campaignId || undefined,
          userId: filter.userId || undefined,
        },
      },
    }
  );

  const [forfeitDeposit, { loading: forfeiting }] = useMutation(
    FORFEIT_DEPOSIT,
    {
      onCompleted: () => {
        alert("예치금이 몰수되었습니다.");
        setShowForfeitModal(false);
        setForfeitReason("");
        setSelectedParticipationId(null);
        refetch();
      },
      onError: (error) => {
        alert(`오류: ${error.message}`);
      },
    }
  );

  const handleForfeit = (participationId: number) => {
    if (!window.confirm("이 참여의 예치금을 몰수하시겠습니까?")) {
      return;
    }
    setSelectedParticipationId(participationId);
    setShowForfeitModal(true);
  };

  const handleConfirmForfeit = () => {
    if (!forfeitReason.trim()) {
      alert("몰수 사유를 입력해주세요.");
      return;
    }

    if (!selectedParticipationId) {
      return;
    }

    forfeitDeposit({
      variables: {
        participationId: selectedParticipationId,
        reason: forfeitReason,
      },
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("ko-KR", {
      style: "currency",
      currency: "KRW",
    }).format(amount);
  };

  // 캠페인별로 그룹핑하고, 각 캠페인 내에서 유저별로 그룹핑
  const groupedData = useMemo((): CampaignGroup[] => {
    const items = (data?.missionAdminFailedParticipationsForForfeit ||
      []) as ForfeitItem[];

    // 1단계: 캠페인별로 그룹핑
    const campaignMap = new Map<
      number,
      {
        campaign: ForfeitItem["participation"]["run"]["campaign"];
        users: Map<number, UserGroup>;
      }
    >();

    items.forEach((item) => {
      const campaignId = item.participation.run.campaign.id;
      const campaign = item.participation.run.campaign;

      if (!campaignMap.has(campaignId)) {
        campaignMap.set(campaignId, {
          campaign,
          users: new Map(),
        });
      }

      const campaignData = campaignMap.get(campaignId);
      if (!campaignData) return;

      const userId = item.participation.user.id;
      const user = item.participation.user;

      if (!campaignData.users.has(userId)) {
        campaignData.users.set(userId, {
          user,
          items: [],
        });
      }

      const userGroup = campaignData.users.get(userId);
      if (userGroup) {
        userGroup.items.push(item);
      }
    });

    // Map을 배열로 변환
    const result: CampaignGroup[] = Array.from(campaignMap.entries()).map(
      ([, data]) => {
        const users: UserGroup[] = Array.from(data.users.entries()).map(
          ([, userData]) => userData
        );

        return {
          campaign: data.campaign,
          users,
        };
      }
    );

    return result;
  }, [data]);

  if (loading) return <div className="text-center py-8">로딩 중...</div>;
  if (error)
    return (
      <div className="text-center py-8 text-red-600">에러: {error.message}</div>
    );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">미션 실패처리</h1>
      </div>

      {/* 필터 */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              캠페인 ID
            </label>
            <input
              type="number"
              value={filter.campaignId || ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  campaignId: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="캠페인 ID 입력"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              유저 ID
            </label>
            <input
              type="number"
              value={filter.userId || ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  userId: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="유저 ID 입력"
            />
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={() => refetch()}
            className="bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700"
          >
            검색
          </button>
        </div>
      </div>

      {/* 캠페인별 그룹 */}
      {groupedData.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          몰수 대상이 없습니다.
        </div>
      ) : (
        <div className="space-y-6">
          {groupedData.map((campaignGroup) => {
            const { campaign, users } = campaignGroup;

            return (
              <div
                key={campaign.id}
                className="bg-white rounded-lg shadow overflow-hidden"
              >
                {/* 캠페인 헤더 */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-4">
                    {campaign.productImageUrl && (
                      <img
                        src={campaign.productImageUrl}
                        alt={campaign.productName}
                        className="w-12 h-12 object-cover rounded"
                      />
                    )}
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {campaign.productName}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {campaign.brand.name}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 테이블 */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          유저이름
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          번호
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          예치금
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          만료된 미션
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          미승인 미션
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          액션
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((userGroup) => {
                        const { user, items } = userGroup;
                        // 한 유저가 같은 캠페인에 여러 참여를 할 수 있으므로 첫 번째 항목 사용
                        const item = items[0];

                        return (
                          <tr
                            key={user.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/ops/mission/reviews/submissions/${item.participationId}`
                              )
                            }
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name || `유저 ${user.id}`}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {user.phone || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(item.participation.depositAmount)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="text-sm text-red-600 font-medium">
                                {item.expiredStepCount}개
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="text-sm text-yellow-600 font-medium">
                                {item.notApprovedStepCount}개
                              </div>
                            </td>
                            <td
                              className="px-4 py-3 text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() =>
                                  handleForfeit(item.participationId)
                                }
                                disabled={forfeiting}
                                className="bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                예치금 몰수
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 몰수 사유 입력 모달 */}
      {showForfeitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">몰수 사유 입력</h3>
            <textarea
              value={forfeitReason}
              onChange={(e) => setForfeitReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
              rows={4}
              placeholder="몰수 사유를 입력하세요"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowForfeitModal(false);
                  setForfeitReason("");
                  setSelectedParticipationId(null);
                }}
                className="flex-1 border border-gray-300 rounded-lg py-2 font-semibold text-gray-700"
              >
                취소
              </button>
              <button
                onClick={handleConfirmForfeit}
                disabled={forfeiting || !forfeitReason.trim()}
                className="flex-1 bg-red-600 text-white rounded-lg py-2 font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {forfeiting ? "처리 중..." : "몰수 처리"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FailuresList;
